import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  BookOpen, Boxes, Code2, Megaphone, FileText, Sparkles, Send, Loader2,
  Lock, ImagePlus, Copy, KeyRound, ArrowLeft, Rocket, Check,
} from "lucide-react";
import { toast } from "sonner";
import { api, local } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";

const ICONS = { BookOpen, Boxes, Code2, Megaphone, FileText, Sparkles };
const PROVIDERS = [
  { id: "openai", label: "GPT" },
  { id: "anthropic", label: "Claude" },
  { id: "gemini", label: "Gemini" },
];

function LockedView({ navigate }) {
  const [key, setKey] = useState("");
  const redeem = async () => {
    try {
      const info = await api.redeemKey({ code: key.trim(), email: local.getEmail() });
      local.setServiceKey(info.code);
      local.setOwnerAccess(!!info.master);
      local.addUnlocked(info.services || []);
      toast.success("تم التفعيل!");
      window.location.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "مفتاح غير صحيح"); }
  };
  return (
    <div className="mx-auto max-w-md rounded-3xl border border-gold/25 bg-amber-400/5 p-8 text-center">
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-400/10 text-gold"><Lock className="h-7 w-7" /></span>
      <h3 className="mt-4 font-display text-lg font-bold text-white">هذه الخدمة مقفلة بمفتاح</h3>
      <p className="mt-2 text-sm text-muted-foreground">أدخل مفتاح التفعيل (IQL) أو اشترِ باقة من المتجر.</p>
      <div className="mt-4 flex gap-2">
        <Input value={key} onChange={(e) => setKey(e.target.value)} placeholder="IQL-XXXX-XXXX-XXXX" className="border-white/10 bg-white/5 font-en text-white" dir="ltr" />
        <Button onClick={redeem} className="btn-gold gap-2 rounded-lg px-4 font-semibold hover:bg-transparent"><KeyRound className="h-4 w-4" /> تفعيل</Button>
      </div>
      <Button onClick={() => navigate("/marketplace")} variant="outline" className="mt-3 w-full rounded-lg border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-white">اذهب إلى المتجر</Button>
    </div>
  );
}

function ChatTool({ service, provider, setProvider }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sid = useRef(local.getEmail() || "guest-" + Math.random().toString(36).slice(2, 8));
  const scroller = useRef(null);
  useEffect(() => { scroller.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [msgs, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const text = input.trim(); setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const r = await api.aiChat({ message: text, provider, service: service.kind, session_id: sid.current });
      setMsgs((m) => [...m, { role: "assistant", content: r.content }]);
    } catch (e) { toast.error("تعذّر الاتصال بالذكاء الاصطناعي"); }
    setLoading(false);
  };

  return (
    <div className="flex h-[62vh] flex-col rounded-2xl border border-white/10 bg-white/[0.02]">
      <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto p-4">
        {msgs.length === 0 && <p className="pt-16 text-center text-sm text-muted-foreground">ابدأ المحادثة — اكتب طلبك بالأسفل.</p>}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
            <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-white/5 text-white" : "border border-cyan/20 bg-cyan/5 text-cyan-50"}`}>{m.content}</div>
          </div>
        ))}
        {loading && <div className="flex justify-end"><div className="rounded-2xl border border-cyan/20 bg-cyan/5 px-4 py-2.5"><Loader2 className="h-4 w-4 animate-spin text-cyan" /></div></div>}
      </div>
      <div className="border-t border-white/5 p-3">
        <div className="mb-2 flex gap-1.5">
          {PROVIDERS.map((p) => (
            <button key={p.id} onClick={() => setProvider(p.id)} className={`rounded-md px-2.5 py-1 text-xs font-medium ${provider === p.id ? "btn-cyan" : "border border-white/10 text-muted-foreground"}`}>{p.label}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <Textarea value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={2} placeholder="اكتب رسالتك…" className="resize-none border-white/10 bg-white/5 text-right text-white" />
          <Button onClick={send} disabled={loading} className="btn-cyan h-auto gap-2 rounded-xl px-4 font-semibold hover:bg-transparent">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TaskTool({ kind, provider, setProvider }) {
  const [prompt, setPrompt] = useState("");
  const [out, setOut] = useState("");
  const [html, setHtml] = useState("");
  const [aql, setAql] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!prompt.trim()) { toast.error("اكتب طلبك أولًا"); return; }
    setLoading(true); setOut(""); setHtml(""); setAql("");
    try {
      if (kind === "contracts") { const r = await api.aiContract({ prompt, provider }); setOut(r.content); setAql(r.aql_code); }
      else if (kind === "page") { const r = await api.aiPage({ prompt, provider }); setHtml(r.html); }
      else if (kind === "builder") { const r = await api.aiBuilder({ prompt, provider }); setOut(r.content); }
      else if (kind === "publisher") { const r = await api.aiPublish({ prompt, provider }); setOut(r.content); }
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التوليد"); }
    setLoading(false);
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="mb-3 flex gap-1.5">
          {PROVIDERS.map((p) => (<button key={p.id} onClick={() => setProvider(p.id)} className={`rounded-md px-2.5 py-1 text-xs font-medium ${provider === p.id ? "btn-cyan" : "border border-white/10 text-muted-foreground"}`}>{p.label}</button>))}
        </div>
        <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={8} placeholder="صِف ما تريد توليده بالتفصيل…" className="resize-none border-white/10 bg-white/5 text-right text-white" />
        <Button onClick={run} disabled={loading} className="btn-cyan mt-3 h-11 w-full gap-2 rounded-xl font-semibold hover:bg-transparent">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} توليد
        </Button>
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-semibold text-white">النتيجة</span>
          {aql && <span className="rounded-md bg-cyan/10 px-2 py-0.5 font-en text-xs text-cyan" dir="ltr">{aql}</span>}
        </div>
        {loading && <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan" /></div>}
        {!loading && out && (
          <div className="max-h-[52vh] overflow-y-auto whitespace-pre-wrap rounded-lg bg-black/20 p-4 text-sm leading-relaxed text-cyan-50">{out}</div>
        )}
        {!loading && html && (
          <iframe title="preview" srcDoc={html} className="h-[52vh] w-full rounded-lg border border-white/10 bg-white" />
        )}
        {!loading && !out && !html && <p className="pt-16 text-center text-sm text-muted-foreground">ستظهر النتيجة هنا.</p>}
      </div>
    </div>
  );
}

function ImageTool({ provider }) {
  const [prompt, setPrompt] = useState("");
  const [img, setImg] = useState("");
  const [loading, setLoading] = useState(false);
  const gen = async () => {
    if (!prompt.trim()) { toast.error("اكتب وصف الصورة"); return; }
    setLoading(true); setImg("");
    try { const r = await api.aiImage({ prompt }); setImg(r.image_base64); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر توليد الصورة الحقيقية"); }
    setLoading(false);
  };
  return (
    <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <h4 className="mb-3 flex items-center gap-2 font-display font-bold text-white"><ImagePlus className="h-5 w-5 text-gold" /> توليد صورة / شعار بالذكاء</h4>
      <div className="flex gap-2">
        <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="مثال: شعار لمزرعة تمور فاخرة بألوان ذهبية" className="border-white/10 bg-white/5 text-right text-white" />
        <Button onClick={gen} disabled={loading} className="btn-gold gap-2 rounded-lg px-4 font-semibold hover:bg-transparent">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} توليد</Button>
      </div>
      {loading && <div className="flex h-56 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>}
      {img && <img src={`data:image/png;base64,${img}`} alt="generated" className="mt-4 mx-auto max-h-80 rounded-xl border border-white/10" />}
    </div>
  );
}

function BuilderTool({ provider, setProvider }) {
  const BUILD_STEPS = [
    "نفهم فكرتك…", "نخطط البنية…", "نجهّز واجهة React…", "ننشئ خادم FastAPI…",
    "نصمم قاعدة البيانات…", "نوصل التكاملات…", "نصمم بنظام AQL4…", "نجري المعاينة…",
  ];
  const [prompt, setPrompt] = useState("");
  const [step, setStep] = useState(-1);
  const [building, setBuilding] = useState(false);
  const [plan, setPlan] = useState("");
  const [html, setHtml] = useState("");
  const timers = useRef([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const build = async () => {
    if (!prompt.trim()) { toast.error("صِف التطبيق أو الموقع الذي تريد بناءه"); return; }
    setBuilding(true); setPlan(""); setHtml(""); setStep(0);
    BUILD_STEPS.forEach((_, i) => { timers.current.push(setTimeout(() => setStep(i), i * 700)); });
    try {
      const [p, pg] = await Promise.all([
        api.aiBuilder({ prompt, provider }),
        api.aiPage({ prompt: `موقع/تطبيق: ${prompt}` }),
      ]);
      setPlan(p.content); setHtml(pg.html);
      toast.success("اكتمل البناء!");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر البناء — تأكد من رصيد الذكاء الاصطناعي"); }
    setBuilding(false); setStep(BUILD_STEPS.length);
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="mb-3 flex items-center gap-2 text-white"><Boxes className="h-5 w-5 text-cyan" /><span className="font-display font-bold">استوديو بناء التطبيقات والمواقع</span></div>
        <p className="mb-3 text-sm text-muted-foreground">صِف فكرتك بلغة طبيعية وسيبني الوكيل خطة كاملة ومعاينة حية للموقع.</p>
        <div className="mb-3 flex gap-1.5">
          {PROVIDERS.map((p) => (<button key={p.id} onClick={() => setProvider(p.id)} className={`rounded-md px-2.5 py-1 text-xs font-medium ${provider === p.id ? "btn-cyan" : "border border-white/10 text-muted-foreground"}`}>{p.label}</button>))}
        </div>
        <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={5} placeholder="مثال: متجر إلكتروني للتمور مع سلة شراء وتقييمات ولوحة تحكم" className="resize-none border-white/10 bg-white/5 text-right text-white" />
        <Button onClick={build} disabled={building} className="btn-cyan mt-3 h-11 gap-2 rounded-xl font-semibold hover:bg-transparent">
          {building ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} ابنِ الآن
        </Button>
        {step >= 0 && (
          <div className="mt-4 space-y-2 rounded-xl border border-white/10 bg-black/20 p-4">
            {BUILD_STEPS.map((s, i) => i <= step && (
              <div key={s} className="flex items-center gap-2 text-sm">
                {i < step ? <Check className="h-4 w-4 text-emerald-400" strokeWidth={3} /> : <Loader2 className="h-4 w-4 animate-spin text-cyan" />}
                <span className={i < step ? "text-muted-foreground" : "text-white"}>{s}</span>
              </div>
            ))}
          </div>
        )}
        {plan && <div className="mt-4 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg bg-black/20 p-4 text-sm leading-relaxed text-cyan-50">{plan}</div>}
      </div>
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0a100d]">
        <div className="flex items-center gap-2 border-b border-white/5 px-4 py-3">
          <div className="flex gap-1.5"><span className="h-3 w-3 rounded-full bg-rose-400/70" /><span className="h-3 w-3 rounded-full bg-amber-400/70" /><span className="h-3 w-3 rounded-full bg-emerald-400/70" /></div>
          <div className="mx-auto flex items-center gap-2 rounded-lg bg-white/5 px-3 py-1 font-en text-xs text-muted-foreground" dir="ltr"><Rocket className="h-3.5 w-3.5" /> aql4.app/preview</div>
        </div>
        {building && <div className="flex h-[52vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-cyan" /></div>}
        {!building && html && <iframe title="preview" srcDoc={html} className="h-[56vh] w-full bg-white" />}
        {!building && !html && <div className="flex h-[52vh] flex-col items-center justify-center text-muted-foreground"><Rocket className="h-12 w-12 opacity-30" /><p className="mt-3 text-sm">ستظهر معاينة موقعك هنا</p></div>}
      </div>
    </div>
  );
}

export default function Services() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [active, setActive] = useState(id || null);
  const [provider, setProvider] = useState("openai");
  const [locked, setLocked] = useState(true);
  const unlocked = local.getUnlocked();

  useEffect(() => {
    api.services().then((d) => { setServices(d.services || []); if (!id && d.services?.[0]) setActive(d.services[0].id); });
    api.config().then((c) => setLocked(c.services_locked !== false)).catch(() => {});
  }, [id]);
  useEffect(() => { if (id) setActive(id); }, [id]);

  const svc = services.find((s) => s.id === active);
  const isUnlocked = svc && (!locked || local.hasOwnerAccess() || unlocked.includes(svc.kind));

  return (
    <main className="relative min-h-screen pt-28">
      <div className="absolute left-1/2 top-20 h-64 w-[700px] -translate-x-1/2 hero-glow blur-3xl" />
      <section className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white"><ArrowLeft className="h-4 w-4 rotate-180" /> الرئيسية</Link>
        <h1 className="mt-4 font-display text-4xl font-extrabold text-white">الخدمات الذكية</h1>
        <p className="mt-1 text-sm text-muted-foreground">كل الأدوات في مكان واحد — مدعومة بذكاء اصطناعي حقيقي.</p>

        <div className="mt-6 flex flex-wrap gap-2">
          {services.map((s) => {
            const Icon = ICONS[s.icon] || Sparkles;
            const on = active === s.id;
            const lock = locked && !local.hasOwnerAccess() && !unlocked.includes(s.kind);
            return (
              <button key={s.id} onClick={() => navigate(`/service/${s.id}`)}
                className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors ${on ? "border-cyan/50 bg-cyan/10 text-cyan" : "border-white/10 text-muted-foreground hover:text-white"}`}>
                <Icon className="h-4 w-4" /> {s.name} {lock && <Lock className="h-3 w-3 text-gold" />}
              </button>
            );
          })}
        </div>

        <div className="mt-8">
          {!svc && <p className="text-center text-muted-foreground">اختر خدمة.</p>}
          {svc && <div className="mb-5 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <h2 className="font-display text-xl font-bold text-white">{svc.name}</h2>
            <p className="mt-2 text-sm leading-7 text-muted-foreground">{svc.desc || "خدمة ذكية متاحة ضمن باقات AQL4، وتعمل بعد تفعيل مفتاح الخدمة."}</p>
          </div>}
          {svc && !isUnlocked && <LockedView navigate={navigate} />}
          {svc && isUnlocked && (
            <>
              {(svc.kind === "notebook" || svc.kind === "notebook-lm") && (
                <>
                  {svc.kind === "notebook-lm" && (
                    <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-cyan/20 bg-cyan/5 p-5">
                      <div>
                        <h3 className="font-display font-bold text-white">استوديو دفتر AQL الكامل</h3>
                        <p className="text-sm text-muted-foreground">مصادر (نص/رابط/ملف) + دردشة مبنية عليها + ملخصات وصوتيات وفيديو حقيقي.</p>
                      </div>
                      <Link to="/notebook" data-testid="open-notebook-studio" className="btn-cyan flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-[#052027]"><BookOpen className="h-4 w-4" /> افتح الاستوديو</Link>
                    </div>
                  )}
                  <ChatTool service={svc} provider={provider} setProvider={setProvider} />
                  {svc.kind === "notebook-lm" && <ImageTool provider={provider} />}
                </>
              )}
              {svc.kind === "builder" && <BuilderTool provider={provider} setProvider={setProvider} />}
              {(svc.kind === "contracts" || svc.kind === "page" || svc.kind === "publisher") && (
                <>
                  <TaskTool kind={svc.kind} provider={provider} setProvider={setProvider} />
                  {svc.kind === "publisher" && <ImageTool provider={provider} />}
                </>
              )}
            </>
          )}
        </div>
      </section>
      <div className="h-20" />
    </main>
  );
}
